import {
  type PageLocaleEntry,
  type WebsiteLocale,
  ALL_WEBSITE_LOCALES,
  BASE_WEBSITE_LOCALES,
  COUNTRY_DISPLAY_BY_CODE,
  type CountryCode,
  PAGE_LOCALE_REGISTRY,
  resolvePageLocales,
} from '@/types/locales'

// Normalise trailing slash so '/buymembership/' matches the same page as '/buymembership'.
const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname

export const getPageLocaleEntry = (pathname: string): PageLocaleEntry | null => {
  const normalised = stripTrailingSlash(pathname)
  return (
    PAGE_LOCALE_REGISTRY.find(
      (page) => normalised === page.path || (page.allowSubpaths && normalised.startsWith(`${page.path}/`)),
    ) ?? null
  )
}

// Paths absent from the registry aren't locale-configurable and serve the default set.
export const getLocalesForPath = (pathname: string): readonly WebsiteLocale[] => {
  const entry = getPageLocaleEntry(pathname)
  return entry ? resolvePageLocales(entry) : BASE_WEBSITE_LOCALES
}

export const isLocaleAllowedOnPath = (locale: WebsiteLocale, pathname: string): boolean =>
  getLocalesForPath(pathname).includes(locale)

// Mirror getPageLocaleEntry's subpath handling so extended locales stay visible
// in the footer picker / mismatch banner on routes like /[locale]/daily30/plans.
export const getLocalesForRoutePattern = (routePattern: string): readonly WebsiteLocale[] => {
  const entry = PAGE_LOCALE_REGISTRY.find(
    (page) =>
      routePattern === page.routePattern || (page.allowSubpaths && routePattern.startsWith(`${page.routePattern}/`)),
  )
  return entry ? resolvePageLocales(entry) : BASE_WEBSITE_LOCALES
}

// Trailing slashes stripped off the configured site URL so joining it with a
// path can never produce a double slash (`https://zoe.com//privacy-policy`).
export const getSiteBaseUrl = (): string => (process.env.NEXT_PUBLIC_SITE_URL ?? '').replace(/\/+$/, '')

// hreflang hrefs must exactly match the served URLs (no trailing slash — Next
// 308s `/foo/` → `/foo`), and match what the sitemap emits, or Google treats
// them as different pages. Path '/' therefore yields `/en-gb`, not `/en-gb/`.
// The locale list defaults from PAGE_LOCALE_REGISTRY so a page's hreflang set
// can't drift from the locales it's actually served in; pass `locales` only for
// paths the registry doesn't know about.
export const generateSEOAlternateUrls = (path: string, locales?: readonly WebsiteLocale[]) => {
  const normalisedPath = stripTrailingSlash(path) === '/' ? '' : stripTrailingSlash(path)
  const resolvedLocales = locales ?? getLocalesForPath(path)
  const siteUrl = getSiteBaseUrl()
  return [
    ...resolvedLocales.map((supportedLocale) => ({
      hreflang: supportedLocale,
      href: `${siteUrl}/${supportedLocale}${normalisedPath}`,
    })),
    { hreflang: 'x-default', href: `${siteUrl}${normalisedPath || '/'}` },
  ] as const
}

export const getLocaleFromCountry = (country: string | undefined): WebsiteLocale | null =>
  country && country in COUNTRY_DISPLAY_BY_CODE ? COUNTRY_DISPLAY_BY_CODE[country as CountryCode].locale : null

const stripQueryAndHash = (path: string): string => path.split(/[?#]/)[0]

const startsWithLocale = (path: string, locale: WebsiteLocale): boolean => {
  const pathname = stripQueryAndHash(path)
  return pathname.startsWith(`/${locale}/`) || pathname === `/${locale}`
}

export const isInLocalisedPath = (pathname: string): boolean => {
  return ALL_WEBSITE_LOCALES.some((locale) => startsWithLocale(pathname, locale))
}

export const isCurrentlyLocalized = (pathname: string, currentLocale: WebsiteLocale): boolean => {
  return startsWithLocale(pathname, currentLocale)
}

export const pathWithoutLocale = (pathname: string): string => {
  return pathname.replace(/^\/[a-z]{2}-[a-z]{2}\//, '')
}

export const hasAnyLocale = (path: string): boolean => {
  return ALL_WEBSITE_LOCALES.some((locale) => startsWithLocale(path, locale))
}

export const getPathWithoutAnyLocale = (path: string): string => {
  for (const locale of ALL_WEBSITE_LOCALES) {
    if (startsWithLocale(path, locale)) {
      return path.replace(`/${locale}`, '')
    }
  }
  return path
}
